import React, { useEffect } from "react";
import { BookOpen, Pencil, Calculator, Globe, Microscope, GraduationCap, Baby, Users, Atom, Triangle, BarChart3, TrendingUp, PieChart } from 'lucide-react';
import { educationLevels } from "src/constants/educationLevels";

interface EducationLevelProps {
  educationLevel: string;
  setEducationLevel: (level: string) => void;
}

const EducationLevel: React.FC<EducationLevelProps> = ({
  educationLevel,
  setEducationLevel,
}) => {
  // Load Nunito font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    const scrollToTop = () => {
      try {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        
        if (window.scrollTo) {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
          });
        }
        
        requestAnimationFrame(() => {
          window.scrollTo(0, 0);
        });
      } catch (error) {
        console.warn('ScrollToTop failed:', error);
      }
    };

    scrollToTop();

    const timeouts = [
      setTimeout(scrollToTop, 0),
      setTimeout(scrollToTop, 10),
      setTimeout(scrollToTop, 50),
      setTimeout(scrollToTop, 100),
      setTimeout(scrollToTop, 200)
    ];

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  return (
    <div className="min-h-screen p-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-gray-800 mb-2" style={{ fontSize: '48px', fontWeight: '400', lineHeight: '1.2' }}>
            What is your education level?
          </h1>
          <div className="flex items-center justify-between">
            <p className="text-gray-600" style={{ fontWeight: '400', fontSize: '16px' }}>
              This helps us recommend appropriate learning materials.
            </p>
            <div className="text-red-500" style={{ fontWeight: '400', fontSize: '16px', transform: 'rotate(-2deg)' }}>
              <span className="text-green-500">✓</span> Click to select
            </div>
          </div>
        </div>

        {/* Vertical Timeline */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <svg width="400" height={`${educationLevels.length * 80 + 40}`} className="overflow-visible">
              {/* Vertical Line */}
              <line
                x1="60"
                y1="20"
                x2="60"
                y2={educationLevels.length * 80 + 20}
                stroke="#374151"
                strokeWidth="3"
              />
              {/* Education Level Boxes */}
              {educationLevels.map((level, index) => {
                const y = 20 + index * 80;
                const isSelected = educationLevel === level.id;
                const IconComponent = level.icon;
                return (
                  <g key={level.id}>
                    {/* Horizontal line to box */}
                    <line
                      x1="60"
                      y1={y + 20}
                      x2="100"
                      y2={y + 20}
                      stroke="#374151"
                      strokeWidth="2"
                    />
                    {/* Selection indicator circle on line */}
                    {isSelected && (
                      <circle
                        cx="60"
                        cy={y + 20}
                        r="6"
                        fill="#10B981"
                        stroke="white"
                        strokeWidth="2"
                      />
                    )}
                    {/* Education level box */}
                    <rect
                      x="100"
                      y={y}
                      width="280"
                      height="40"
                      fill={isSelected ? level.selectedColor : level.color}
                      stroke={isSelected ? level.selectedColor : level.borderColor}
                      strokeWidth="2"
                      rx="8"
                      className="cursor-pointer transition-all duration-200 hover:opacity-80"
                      onClick={() => setEducationLevel(level.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    {/* Selection checkmark */}
                    {isSelected && (
                      <g>
                        <circle cx="365" cy={y + 8} r="8" fill="#10B981" />
                        <path
                          d={`M ${365 - 3} ${y + 8} L ${365 - 1} ${y + 10} L ${365 + 3} ${y + 6}`}
                          stroke="white"
                          strokeWidth="2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                    )}
                    {/* Text content */}
                    <foreignObject 
                      x="110" 
                      y={y + 5} 
                      width="260" 
                      height="30"
                      className="cursor-pointer"
                      onClick={() => setEducationLevel(level.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="flex items-center gap-3 h-full pointer-events-none">
                        <IconComponent 
                          className="w-5 h-5 flex-shrink-0" 
                          style={{ color: isSelected ? 'white' : '#374151' }}
                        />
                        <span 
                          className="text-sm leading-tight" 
                          style={{ 
                            fontWeight: '500',
                            color: isSelected ? 'white' : '#374151'
                          }}
                        >
                          {level.label}
                        </span>
                      </div>
                    </foreignObject>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationLevel;
